import React, { forwardRef, useCallback, useImperativeHandle, useState } from 'react';
import type { Dispatch, SetStateAction } from 'react';

import { TooltipPositions } from '../../components/tooltips/tooltipConstants';
import FloatingPanel from '../../components/tooltips/FloatingPanel';
import PopupPanelMouseManager from '../../components/popupPanel/PopupPanelMouseManager';
import Button from '../../components/buttons/Button';
import Icon from '../../components/icons/Icon';
import useListFocusManager from '../../components/lists/useListFocusManager';
import FormulaDropdownListItem from './FormulaDropdownListItem';
import { PopupIds } from '../../components/popupPanel/popupConstants';
import { useTranslator } from '../../components/i18n/useTranslator';
import { hyperFormulaList } from './manager/hyperFormulaInstance';
import { getMatchingFormulas } from './formulaDropdownSuggestionUtils';
import useFormulaDropdownKeyboardControls from './useFormulaDropdownKeyboardControls';

import './FormulaDropdown.scss';

export type FormulaDropdownRef = {
    setFormulaDropdownQuery: (query?: string) => boolean;
    hideFormulaDropdown: () => void;
};

export type FormulaDropdownProps = {
    addFormulaToCell: (formula: string, formulaQuery: string) => void;
    showFormulaDropdown: boolean;
    setShowFormulaDropdown: Dispatch<SetStateAction<boolean>>;
};

const FormulaDropdown = forwardRef<FormulaDropdownRef, FormulaDropdownProps>(function FormulaDropdownContent(
    { addFormulaToCell, showFormulaDropdown, setShowFormulaDropdown },
    ref,
) {
    const [formulaDropdownData, setFormulaDropdownData] = useState<string[]>(hyperFormulaList);
    const [formulaQuery, setFormulaQuery] = useState('');

    const listFocusManager = useListFocusManager({
        loop: true,
        initialFocusIndex: 0,
        listSize: formulaDropdownData.length,
    });
    const { focusedIndex, setFocusIndex } = listFocusManager;

    const translate = useTranslator('popup', 'table.formula');

    const showDropdown = showFormulaDropdown && formulaDropdownData.length > 0;

    const setFormulaDropdownQuery = useCallback(
        (query = ''): boolean => {
            setShowFormulaDropdown(true);
            setFocusIndex(0);
            setFormulaQuery(query);

            const newFormulaList = getMatchingFormulas(query, hyperFormulaList);
            setFormulaDropdownData(newFormulaList);

            return newFormulaList.length > 0;
        },
        [setFocusIndex, setShowFormulaDropdown],
    );

    const hideFormulaDropdown = useCallback(() => {
        setShowFormulaDropdown(false);
        setFormulaQuery('');
        setFormulaDropdownData([]);
    }, [setShowFormulaDropdown]);

    const selectFormula = useCallback(
        (formula?: string) => {
            if (!formula) return;

            addFormulaToCell(formula, formulaQuery);
            hideFormulaDropdown();
        },
        [addFormulaToCell, formulaQuery, hideFormulaDropdown],
    );

    useFormulaDropdownKeyboardControls({
        enabled: showDropdown,
        focusedFormula: formulaDropdownData[focusedIndex],
        listFocusManager,
        selectFormula,
    });

    useImperativeHandle(ref, () => ({ setFormulaDropdownQuery, hideFormulaDropdown }), [
        hideFormulaDropdown,
        setFormulaDropdownQuery,
    ]);

    return (
        <>
            {showDropdown && (
                <FloatingPanel
                    arrowColor="var(--ui-modal-background-primary)"
                    arrowStrokeColor="var(--ui-modal-background-primary-hover)"
                    className="FormulaDropdown"
                    selector=".Table.selected .handsontable-input-holder"
                    position={TooltipPositions.BOTTOM}
                    pollPosition
                >
                    <PopupPanelMouseManager
                        addFormulaToCell={addFormulaToCell}
                        showFormulaDropdown={showFormulaDropdown}
                        setShowFormulaDropdown={setShowFormulaDropdown}
                        visible={showDropdown}
                        popupId={PopupIds.FORMULA_DROPDOWN}
                        closePopup={hideFormulaDropdown}
                    >
                        <ul className="formula-list" data-table-util={true}>
                            {formulaDropdownData.map((formula, index) => (
                                <FormulaDropdownListItem
                                    key={formula}
                                    formulaQuery={formulaQuery}
                                    focusedIndex={focusedIndex}
                                    formula={formula}
                                    index={index}
                                    onSelectFormula={selectFormula}
                                />
                            ))}
                        </ul>
                        <div className="formula-docs-link">
                            <Button
                                onMouseDown={() => {
                                    const newWindow = window.open(
                                        'https://help.milanote.com/en/articles/8584809-formulas-functions-for-tables',
                                        '_blank',
                                        'noopener,noreferrer',
                                    );

                                    if (newWindow) {
                                        newWindow.opener = null;
                                    }
                                }}
                            >
                                <span>{translate('helpLink')}</span>
                                <Icon name="chevron-right" />
                            </Button>
                        </div>
                    </PopupPanelMouseManager>
                </FloatingPanel>
            )}
        </>
    );
});

export default FormulaDropdown;
